import { Box, MenuItem, Pagination, TextField, Typography } from "@mui/material";
import DashboardLayout from "layout";
import { useQuery } from "@tanstack/react-query";
import {
  getHotelBookings,
  HOTEL_STATUSES,
  HOTEL_STATUS_LABELS,
  HOTEL_STATUS_TONES,
  HotelBooking,
  nightsBetween,
} from "services/hotel";
import { useState } from "react";
import TableSearch from "shared/TableSearch";
import DataTable, { DataColumn } from "components/DataTable";
import StatusBadge from "components/StatusBadge";
import useResettableState from "hooks/useResettableState";
import { formatNaira } from "services/finance";
import { formatDisplayDate, formatUsd } from "utils/helpers";
import HotelBookingPanel from "./HotelBookingPanel";

// IDs look like HOTEL-<timestamp>-<hash>; sandbox bookings append the ETG test
// scenario (e.g. -unknown_soldout), which isn't part of the ID admins look up.
const shortOrderId = (id: string) => (id || "").split("-").slice(0, 3).join("-");

const columns: DataColumn<HotelBooking>[] = [
  {
    label: "Hotel",
    render: (b) => (
      <>
        {b.hotel_name}
        <Typography fontSize={11} color="#667085" whiteSpace="nowrap">
          {shortOrderId(b.partner_order_id)}
        </Typography>
      </>
    ),
  },
  {
    label: "Guest",
    render: (b) => (
      <>
        {b.contact_info?.name || "N/A"}
        <Typography fontSize={12} color="#667085">
          {b.contact_info?.email}
        </Typography>
      </>
    ),
  },
  {
    label: "Stay",
    render: (b) => (
      <>
        {formatDisplayDate(b.checkin, false)} → {formatDisplayDate(b.checkout, false)}
        <Typography fontSize={12} color="#667085">
          {nightsBetween(b.checkin, b.checkout)} night(s) · {b.rooms} room(s)
        </Typography>
      </>
    ),
  },
  {
    label: "Amount",
    render: (b) => (
      <>
        {formatNaira(b.total_price)}
        {b.total_price_usd ? (
          <Typography fontSize={12} color="#667085">
            {formatUsd(b.total_price_usd)}
          </Typography>
        ) : null}
      </>
    ),
  },
  {
    label: "Status",
    render: (b) => (
      <StatusBadge status={b.status} tones={HOTEL_STATUS_TONES} labels={HOTEL_STATUS_LABELS} />
    ),
  },
  { label: "Date", render: (b) => formatDisplayDate(b.createdAt) },
];

const HotelBookings = () => {
  const [status, setStatus] = useState("");
  const [page, setPage] = useResettableState(1, status);
  const [searchValue, setSearchValue] = useState("");
  const [selected, setSelected] = useState<HotelBooking | null>(null);
  const limit = 20;

  const { data: bookingsResponse, isLoading } = useQuery({
    queryKey: ["hotelBookings", page, status],
    queryFn: () => getHotelBookings(page, limit, status),
    placeholderData: (prev) => prev,
  });

  const bookings = bookingsResponse?.data?.bookings || [];
  const total = bookingsResponse?.data?.total || 0;
  const totalPages = Math.max(Math.ceil(total / limit), 1);

  // The admin endpoint has no search param, so this only filters the current page.
  const q = searchValue.trim().toLowerCase();
  const filtered = q
    ? bookings.filter((b) =>
        [b.partner_order_id, b.hotel_name, b.contact_info?.email].some((v) =>
          String(v || "").toLowerCase().includes(q),
        ),
      )
    : bookings;

  return (
    <DashboardLayout nextSideBar>
      <Box sx={{ width: "100%", p: { xs: 0, sm: 2 } }}>
        <Box mb={3}>
          <Typography variant="h5" fontWeight={600} color="#101828">
            Hotel Bookings
          </Typography>
          <Typography variant="body2" color="#475467">
            View and manage hotel bookings
          </Typography>
        </Box>

        <Box display="flex" flexWrap="wrap" alignItems="center" gap="12px">
          <Box flex={1} minWidth={{ xs: "100%", sm: 300 }}>
            <TableSearch
              noBgColor
              p="10px 0"
              searchPlaceHolder="Search this page by order ID, hotel, or email..."
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              hideFilter
              hideDate
            />
          </Box>
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {HOTEL_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {HOTEL_STATUS_LABELS[s] || s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ")}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box mt="12px">
          <DataTable
            columns={columns}
            rows={filtered}
            loading={isLoading}
            emptyText="No hotel bookings found"
            rowKey={(b) => b._id || b.partner_order_id}
            onRowClick={setSelected}
          />
        </Box>

        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      </Box>

      {selected && <HotelBookingPanel booking={selected} onClose={() => setSelected(null)} />}
    </DashboardLayout>
  );
};

export default HotelBookings;
